import { useEffect, useCallback, useReducer } from 'react';
import axios from 'axios';
import shuffle from 'lodash/shuffle';
import { BannerData, Offer } from '@wix/dealer-common/dist/src/interfaces';

function sendCommand(command: string, realEstateId: string) {
  if (window.parent.postMessage) {
    const payload = {
      type: 'dealerAd',
      command,
      realEstateId,
    };
    window.parent.postMessage(payload, '*');
  }
}

interface State {
  isEmpty: boolean;
  isLoaded: boolean;
  banners: BannerData[];
  offers: Offer[];
}

interface Action {
  type: string;
  banners?: BannerData[];
  bannerId?: string;
  offers?: Offer[];
  offerId?: string;
}

function dealerAdReducer(
  state: State,
  { type, banners = [], bannerId, offers = [], offerId }: Action
) {
  switch (type) {
    case 'empty':
      return state.isEmpty
        ? state
        : {
            isLoaded: true,
            banners: [],
            isEmpty: true,
            offers: [],
          };
    case 'load':
      return state.isLoaded
        ? state
        : {
            isLoaded: true,
            banners,
            isEmpty: banners.length === 0 && offers.length === 0,
            offers,
          };
    case 'close':
      const filteredBanners = state.banners.filter(
        b => b.bannerId !== bannerId
      );
      return bannerId && filteredBanners.length !== state.banners.length
        ? {
            ...state,
            banners: filteredBanners,
            isEmpty: filteredBanners.length > 0,
          }
        : state;
    case 'closeOffer':
      const filteredOffers = state.offers.filter(b => b.offerGuid !== offerId);
      return offerId && filteredOffers.length !== state.offers.length
        ? {
            ...state,
            offers: filteredOffers,
            isEmpty: filteredOffers.length > 0,
          }
        : state;
    default:
      return state;
  }
}

export function useDealerBanners(realEstateId: string, userGuid = '') {
  const [{ isLoaded, banners, isEmpty }, dispatch] = useReducer(
    dealerAdReducer,
    { isLoaded: false, isEmpty: false, banners: [], offers: [] }
  );

  const onEmpty = useCallback(() => {
    dispatch({ type: 'empty' });
    sendCommand('ad-empty', realEstateId);
  }, [realEstateId]);

  const closeBanner = useCallback((bannerToClose: BannerData) => {
    dispatch({ type: 'close', bannerId: bannerToClose.bannerId });
  }, []);

  const loadBanners = useCallback((newBanners: BannerData[]) => {
    dispatch({ type: 'load', banners: newBanners });
  }, []);

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    axios
      .create(/* do not use wix axios config as it messes up CORS OPTIONS request for preflight */)
      .get('https://www.wix.com/_api/wix-dealer-webapp/da/offers', {
        headers: {},
        params: {
          realEstateId: realEstateId,
          userGuid: userGuid,
        },
      })
      .then(response => {
        const offers = response?.data?.dealerData?.[realEstateId]?.offers;

        if (offers) {
          loadBanners(shuffle(offers));
          sendCommand('ad-filled', realEstateId);
        } else {
          onEmpty();
        }
      })
      .catch(onEmpty);
  }, [loadBanners, onEmpty, realEstateId, userGuid, isLoaded]);

  return {
    onEmpty,
    closeBanner,
    isLoaded,
    banners,
    isEmpty,
  };
}

export function useDealerBannersV2(
  realEstateId: string,
  biClientIdSigned: string,
  userGuid = ''
) {
  const [{ isLoaded, banners, isEmpty, offers }, dispatch] = useReducer(
    dealerAdReducer,
    {
      isLoaded: false,
      isEmpty: false,
      banners: [],
      offers: [],
    }
  );

  const onEmpty = useCallback(() => {
    dispatch({ type: 'empty' });
    sendCommand('ad-empty', realEstateId);
  }, [realEstateId]);

  const closeOffer = useCallback((offerToClose: Offer) => {
    dispatch({ type: 'closeOffer', offerId: offerToClose.offerGuid });
  }, []);

  const loadOffers = useCallback((newOffers: Offer[]) => {
    dispatch({ type: 'load', offers: newOffers });
  }, []);

  useEffect(() => {
    if (isLoaded) {
      return;
    }

    axios
      .create(/* do not use wix axios config as it messes up CORS OPTIONS request for preflight */)
      .get(
        'https://www.wix.com/_api/dealer-offers-serving-service/proxy/v1/dealer/serving/offers',
        {
          headers: {},
          params: {
            realEstateId: realEstateId,
            deviantArtUserId: biClientIdSigned,
          },
        }
      )
      .then(response => {
        const offersResponse = response?.data?.offers;
        if (offersResponse && offersResponse.length > 0) {
          loadOffers(shuffle(offersResponse));
          sendCommand('ad-filled', realEstateId);
        } else {
          onEmpty();
        }
      })
      .catch(onEmpty);
  }, [loadOffers, onEmpty, realEstateId, userGuid, isLoaded, biClientIdSigned]);

  return {
    onEmpty,
    closeOffer,
    isLoaded,
    banners,
    isEmpty,
    offers,
  };
}
